import isNil from 'lodash/isNil';
import isEqual from 'lodash/isEqual';
import downloadFile from 'downloadjs';

import { danger, success } from 'redux-store/reducers/common-reducer';
import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';
import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';
import {
  fetchDashboardLists,
  fetchListInfluencers,
  saveList,
  updateLightweightInfluencersImportTasks
} from 'modules/lists/reducers/lists';

import {
  ADD_EMPTY_ENTITY_VALUE_ACTION,
  CHANGE_MODAL_TAB_SECTION_ACTION,
  CHANGE_PAGE_ACTION,
  DELETE_ENTITY_ACTION,
  GO_BACK_TO_INITIAL_UPLOAD_STEP_ACTION,
  GO_TO_SUCCESSFUL_UPLOAD_STEP_ACTION,
  REMOVE_UPLOADED_FILE,
  TOGGLE_AGREE_TO_CREATE_NEW_COLLECTION,
  TOGGLE_EXPLANATION_BANNER_ACTION,
  TOGGLE_MODAL_ACTION,
  UPDATE_ENTITIES_ACTION,
  UPDATE_ENTITY_VALUE_ACTION,
  UPDATE_GENERAL_COUNTRIES_LIST_ACTION
} from '../constants/actions';
import {
  CREATE_NEW_PROFILES_MODAL_MANUAL_IDENTIFIER,
  XLSX_TEMPLATE_URL
} from '../constants/common';
import {
  CREATE_NEW_PROFILES_EMPTY_PROFILE_ENTITY
} from '../constants/entity';


import { API, get, post } from 'utils/api';
import { logError } from 'utils/log';
import { navigate } from 'libs/router';
import { countrySearchToDropdownItems } from 'utils/locations';
import { __t, applyTranslations } from 'utils/language';
import {
  getEntitiesFromState,
  getEntityValidationResult,
  toNonNullableEntities
} from '../utils/entities';

import {
  CreateNewProfilesTabIdentifier,
  UpdateEntityValue
} from '../types/general';
import {
  CreateNewProfilesEntity,
  CreateNewProfilesOpenedOn
} from '../types/CreateNewProfiles.type';
import {
  Action,
  List,
  ListImportTask,
  ListMeta,
  MixedSelectDropdownItem,
  PromiseAction,
  ThunkAction
} from 'types';

applyTranslations(require('./actions.i18n.json'));

export const toggleModal = (
  activeModal:
    | null
    | { type: 'add-entities', openedOn: CreateNewProfilesOpenedOn }
    | { type: 'confirmation' }
    | { type: 'new-collection' }
) => ({
  type: TOGGLE_MODAL_ACTION,
  payload: { activeModal }
});

const updateGeneralCountriesList = (countries: Array<MixedSelectDropdownItem>) => ({
  type: UPDATE_GENERAL_COUNTRIES_LIST_ACTION,
  payload: { countries }
});

export const openAddEntitiesModal = (
  openedOn: CreateNewProfilesOpenedOn
): ThunkAction => async dispatch => {
  try {
    const countries = await countrySearchToDropdownItems('');

    dispatch(updateGeneralCountriesList(countries));
    dispatch(toggleModal({ type: 'add-entities', openedOn }));
  } catch (error) {
    logError(error);
  }
};

export const changeModalTabSection = (
  tab: CreateNewProfilesTabIdentifier
) => ({
  type: CHANGE_MODAL_TAB_SECTION_ACTION,
  payload: { tab }
});

export const goBackToInitialUploadStep = () => ({
  type: GO_BACK_TO_INITIAL_UPLOAD_STEP_ACTION
});

export const goToSuccessfulUploadStep = () => ({
  type: GO_TO_SUCCESSFUL_UPLOAD_STEP_ACTION
});

export const updateEntity = (value: UpdateEntityValue) => ({
  type: UPDATE_ENTITY_VALUE_ACTION,
  payload: { value }
});

export const addEmptyEntity = () => ({
  type: ADD_EMPTY_ENTITY_VALUE_ACTION
});

export const deleteEntity = (index: number) => ({
  type: DELETE_ENTITY_ACTION,
  payload: { index }
});

export const toggleExplanationBanner = () => ({
  type: TOGGLE_EXPLANATION_BANNER_ACTION
});

export const changePage = (page: number) => ({
  type: CHANGE_PAGE_ACTION,
  payload: { page }
});

export const toggleAgreeToCreateNewCollection = (value: boolean) => ({
  type: TOGGLE_AGREE_TO_CREATE_NEW_COLLECTION,
  payload: { value }
});

export const removeUploadedFile = () => ({
  type: REMOVE_UPLOADED_FILE
});

export const closeAddEntitiesModal = (): ThunkAction => (dispatch, getState) => {
  const entities = getEntitiesFromState(getState().createNewProfiles);

  if (entities.length > 0 && !isEqual(entities, [CREATE_NEW_PROFILES_EMPTY_PROFILE_ENTITY])) {
    dispatch(showConfirmModal({
      title: __t('create_new_profiles_reducers_actions.confirm_modal.title'),
      message: __t('create_new_profiles_reducers_actions.confirm_modal.message'),
      confirmButtonLabel: __t('create_new_profiles_reducers_actions.confirm_modal.confirm_button'),
      onCancel: () => dispatch(hideConfirmModal()),
      onConfirm: () => {
        dispatch(toggleModal(null));
        dispatch(hideConfirmModal());
      }
    }));
  } else {
    dispatch(toggleModal(null));
  }
};

const updateEntitiesForModal = (
  entities: Array<CreateNewProfilesEntity>,
  filename?: string
) => ({
  type: UPDATE_ENTITIES_ACTION,
  payload: {
    entities,
    filename
  }
});

export const uploadFileForParsing = (file: File | null): ThunkAction => async (
  dispatch,
  getState
) => {
  if (file === null) {
    return;
  }

  const state = getState().createNewProfiles;
  const currentOrganization = getState().organizations.currentOrganization;

  if (currentOrganization === null) {
    return;
  }

  const url = API.END_POINTS.LISTS.uploadXLSToParseLightweightInfluencers(currentOrganization.id);

  try {
    const data = new FormData();

    data.append('file', file);

    if (state.activeModal !== 'add-entities') {
      throw new Error('Impossible operation! CreateNewProfilesModal is not open');
    }

    if (state.createEntitiesModal.type === CREATE_NEW_PROFILES_MODAL_MANUAL_IDENTIFIER) {
      throw new Error('Impossible operation! File upload tab of CreateNewProfilesModal is not open');
    }

    dispatch(showLoading(url));

    const response = await post(url, { data }, { suppressGlobalNotification: true });

    dispatch(updateEntitiesForModal(
      response?.data?.influencers ?? [],
      file.name
    ));
    dispatch(hideLoading(url));
  } catch (error) {
    logError(error);
    dispatch(danger(__t('create_new_profiles_reducers_actions.message_failed')));
    dispatch(updateEntitiesForModal([], file.name));
    dispatch(hideLoading(url));
  }
};

export const downloadTemplate = (): ThunkAction => () => {
  try {
    downloadFile(XLSX_TEMPLATE_URL);
  } catch (err) {
    logError(err);
  }
};

export const moveToConfirmation = (): ThunkAction => async (dispatch, getState) => {
  const { list } = getState().lists.currentList;
  const entities = getEntitiesFromState(getState().createNewProfiles);

  if (isNil(list)) {
    return;
  }

  const validationResult = getEntityValidationResult(entities);

  if (!validationResult[0]) {
    dispatch(updateEntitiesForModal(
      validationResult[1]
    ));

    return;
  }

  dispatch(toggleModal({ type: 'confirmation' }));
};

const setWatchDogForATask = (
  taskId: number,
  orgId: number,
  collectionId: number | undefined,
  dispatch: (action: Action | ThunkAction | PromiseAction) => any
) => {
  const CHECK_EVERY = 1000 * 60;
  const handler = async () => {
    try {
      const url = API.END_POINTS.LISTS.getImportInfluencersTaskInfo(orgId, taskId);
      const response = await get(url, {}, { suppressGlobalNotification: true });
      const data: ListImportTask | null = response?.data ?? null;

      if (data && data.status !== 'IN_PROGRESS') {
        const collectionUrl = `/collections/creators/${data.listId}?prev=lists&orgId=${orgId}`;

        if (!isNil(collectionId)) {
          await dispatch(fetchListInfluencers(orgId, collectionId));
        }
        dispatch(success(
          __t('create_new_profiles_reducers_actions.successful_import', {
            href: collectionUrl
          }),
          { keepBetweenPages: true }
        ));
        dispatch(updateLightweightInfluencersImportTasks([response.data]));
      } else {
        setTimeout(handler, CHECK_EVERY);
      }
    } catch (error) {
      logError(error);
    }
  };

  handler();
};

const saveEntities = (
  listId?: number
): ThunkAction => async (dispatch, getState) => {
  const LOADING_KEY = 'create-new-profiles-save-entities';

  try {
    const entities = getEntitiesFromState(getState().createNewProfiles);
    const currentOrganization = getState().organizations.currentOrganization;

    if (currentOrganization === null) {
      return;
    }

    const url = API.END_POINTS.LISTS.saveBulkLightweightInfluencers(currentOrganization.id);
    const data = {
      influencers: toNonNullableEntities(entities),
      listId
    };

    dispatch(showLoading(LOADING_KEY));
    const response = await post(
      url,
      { data },
      { suppressGlobalNotification: true }
    );
    dispatch(toggleModal(null));
    dispatch(hideLoading(LOADING_KEY));
    dispatch(updateLightweightInfluencersImportTasks([response.data]));
    setWatchDogForATask(
      response.data.taskId,
      currentOrganization.id,
      listId,
      dispatch
    );
  } catch (err) {
    logError(err);
    dispatch(danger(__t('create_new_profiles_reducers_actions.message_failed')));
    dispatch(hideLoading(LOADING_KEY));
  }
};

export const moveToCreateCollection = (): ThunkAction => async (dispatch, getState) => {
  const { confirmationModal, openedOn } = getState().createNewProfiles;

  if (openedOn !== 'custom-collection' && confirmationModal?.wantToCreateNew) {
    dispatch(toggleModal({ type: 'new-collection' }));
  }
};

export const saveToCustomCollection = (): ThunkAction => async (dispatch, getState) => {
  const { list } = getState().lists.currentList;

  if (isNil(list)) {
    return;
  }

  await dispatch(saveEntities(list.id));
};

export const saveToSmartCollection = (): ThunkAction => async (dispatch, getState) => {
  const { currentOrganization } = getState().organizations;

  if (isNil(currentOrganization)) {
    return;
  }

  await dispatch(saveEntities());

  const lists: Array<List> = await dispatch(fetchDashboardLists(
    currentOrganization.id,
    'smart',
    'brand',
    1,
    'updated_at.desc',
    '',
    100,
    false
  ));

  const list = lists.find(l => l.smartListType === 'my_created_profiles') ?? null;

  if (list === null) {
    return;
  }

  navigate.to(`/collections/creators/${list.id}?prev=lists&orgId=${currentOrganization.id}`);
};

export const saveCollectionAndSaveEntitiesToIt = (
  list: ListMeta,
  organizationId: number,
  listId?: number | null,
  shouldUpdate?: boolean | null
): ThunkAction => async (dispatch, getState) => {
  try {
    const { currentOrganization } = getState().organizations;

    if (isNil(currentOrganization)) {
      return;
    }

    const createdCollection = await dispatch(saveList(
      list, organizationId, listId ?? undefined, shouldUpdate ?? false
    ));

    await dispatch(saveEntities(createdCollection.id));
    await dispatch(fetchListInfluencers(
      currentOrganization.id, createdCollection.id
    ));

    navigate.to(`/collections/creators/${createdCollection.id}?prev=lists&orgId=${currentOrganization.id}`);
  } catch (error) {
    logError(error);
    dispatch(danger(__t('create_new_profiles_reducers_actions.message_failed')));
  }
};
